/**
 * Tab panels are all rendered visible in the HTML so crawlers index every response
 * shape and every code sample. This collapses them to real tabs once JS is running.
 */
export function initTabs(): void {
  for (const group of document.querySelectorAll<HTMLElement>('[data-tabs]')) {
    const buttons = [...group.querySelectorAll<HTMLButtonElement>('.tab')];
    const panels = [...group.querySelectorAll<HTMLElement>('.tab-panel')];
    if (buttons.length !== panels.length || !buttons.length) continue;

    // Windowed sample groups show the active language's filename on the right of the bar.
    const filenameEl = group.querySelector<HTMLElement>('.code-file');

    const select = (index: number, focus = false) => {
      buttons.forEach((button, i) => {
        const active = i === index;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && focus) button.focus();
      });
      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      const filename = buttons[index]?.dataset.filename;
      if (filenameEl && filename) filenameEl.textContent = filename;
    };

    const preferred = group.dataset.tabs === 'samples' ? readPreferredLanguage(buttons) : 0;
    select(preferred);

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        select(index);
        if (group.dataset.tabs === 'samples') {
          savePreferredLanguage(button.textContent?.trim() ?? '');
          syncOtherSampleGroups(group, index);
        }
      });

      button.addEventListener('keydown', (event) => {
        const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!delta) return;
        event.preventDefault();
        select((index + delta + buttons.length) % buttons.length, true);
      });
    });
  }
}

const LANG_KEY = 'lp-docs-sample-language';

function readPreferredLanguage(buttons: HTMLButtonElement[]): number {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (!stored) return 0;
    const index = buttons.findIndex((b) => b.textContent?.trim() === stored);
    return index === -1 ? 0 : index;
  } catch {
    return 0;
  }
}

function savePreferredLanguage(label: string): void {
  try {
    localStorage.setItem(LANG_KEY, label);
  } catch {
    /* ignore */
  }
}

function syncOtherSampleGroups(source: HTMLElement, index: number): void {
  for (const group of document.querySelectorAll<HTMLElement>('[data-tabs="samples"]')) {
    if (group === source) continue;
    const button = group.querySelectorAll<HTMLButtonElement>('.tab')[index];
    button?.click();
  }
}
